import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import logo from "../images/logo8-rm-bg.png";
import { Link, Outlet } from "react-router-dom";
import Dropdown from "./Dropdown";

const navLinks = [
  { path: "/", text: "Home" },
  { path: "/products", text: "Products" },
  { path: "/contact", text: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [menuOpen, toggleMenuOpen] = useState(false);
  const hideTimer = useRef(null);
  const show = () => {
    clearTimeout(hideTimer.current);
    toggleMenuOpen(true);
  };
  const hide = () => {
    hideTimer.current = setTimeout(() => toggleMenuOpen(false), 150);
  };

  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const r = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMobile = () => setIsMenuOpen((v) => !v);

  return (
    <>
      <Bar scrolled={scrolled}>
        <Inner>
          <Link to="/" onClick={hide}>
            <img src={logo} alt="BR Plast" className="logo" />
          </Link>

          {mobile ? (
            <>
              <HamBtn onClick={toggleMobile} aria-label="Menu">
                {isMenuOpen ? (
                  <span style={{ fontSize: 22, lineHeight: 1 }}>×</span>
                ) : (
                  <>
                    <div /><div /><div />
                  </>
                )}
              </HamBtn>
              {isMenuOpen && (
                <MobileMenu>
                  {navLinks.map((l) => (
                    <MobileItem key={l.path}>
                      <Link to={l.path} onClick={toggleMobile}>{l.text}</Link>
                    </MobileItem>
                  ))}
                  <MobileItem>
                    <Link to="/contact" onClick={toggleMobile} className="mobile-cta">
                      Get a Quote
                    </Link>
                  </MobileItem>
                </MobileMenu>
              )}
            </>
          ) : (
            <DesktopNav>
              <NavLink to="/" onClick={hide} onMouseEnter={hide}>Home</NavLink>

              <ProductWrapper onMouseEnter={show} onMouseLeave={hide}>
                <NavLink to="products" onClick={hide}>Products</NavLink>
                {menuOpen && <Dropdown onMouseLeave={hide} />}
              </ProductWrapper>

              <NavLink to="contact" onClick={hide} onMouseEnter={hide}>Contact</NavLink>
              <QuoteLink to="contact" onClick={hide} onMouseEnter={hide}>
                Get a Quote
              </QuoteLink>
            </DesktopNav>
          )}
        </Inner>
      </Bar>

      <div className={`outlet ${isMenuOpen ? "hidden" : ""}`}>
        <Outlet />
      </div>
    </>
  );
};

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: var(--navbar-h);
  background: ${(p) => (p.scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.95)")};
  border-bottom: 1px solid ${(p) => (p.scrolled ? "var(--color-border)" : "transparent")};
  backdrop-filter: blur(12px);
  transition: border-color 0.25s ease;
`;

const Inner = styled.div`
  height: 100%;
  padding: 0 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .logo {
    height: 48px;
    display: block;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 0 20px;
    .logo { height: 40px; }
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-muted);
  padding: 8px 14px;
  border-radius: 4px;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    color: var(--color-dark);
    background: var(--color-bg-subtle);
  }
`;

const QuoteLink = styled(Link)`
  text-decoration: none;
  margin-left: 8px;
  background: var(--color-dark);
  color: white;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 20px;
  border-radius: var(--radius-btn);
  letter-spacing: 0.3px;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    background: var(--color-brand);
    transform: translateY(-1px);
    color: white;
  }
`;

const ProductWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HamBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0;

  div {
    width: 22px;
    height: 2px;
    background: var(--color-dark);
    margin: 3px 0;
    border-radius: 2px;
    transition: var(--transition);
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: calc(var(--navbar-h) + 4px);
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  z-index: 99;
`;

const MobileItem = styled.div`
  border-bottom: 1px solid var(--color-border);

  &:last-child { border-bottom: none; }

  a {
    display: block;
    padding: 16px 24px;
    font-size: 15px;
    font-weight: 500;
    color: var(--color-text);
    text-decoration: none;
    transition: var(--transition);

    &:hover { background: var(--color-bg-subtle); color: var(--color-dark); }
  }

  a.mobile-cta {
    background: var(--color-dark);
    color: white;
    font-weight: 700;
    text-align: center;

    &:hover { background: var(--color-brand); }
  }
`;

export default Navbar;
